<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import type { BoardSummary } from '../../lib/types';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import Icon from '../../components/Icon.svelte';
  import { viewport, coarsePointer } from '../../lib/stores/viewport';

  interface Props {
    boards: BoardSummary[];
  }

  let { boards }: Props = $props();

  let creating = $state(false);
  let newName = $state('');

  function createBoard(event: SubmitEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;

    router.post(
      '/boards',
      { board: { name } },
      {
        onFinish: () => {
          creating = false;
          newName = '';
        },
      },
    );
  }

  function deleteBoard(board: BoardSummary) {
    if (!confirm(`Delete "${board.name}"? This cannot be undone.`)) return;
    router.delete(`/boards/${board.id}`);
  }

  function formatUpdated(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
</script>

<svelte:head><title>Boards · Momentium</title></svelte:head>

<div class="page">
  {#if $viewport.isMobile}
    <MobileTopBar title="Boards">
      {#snippet actions()}
        <button
          type="button"
          class="mtb-icon-btn"
          aria-label="New board"
          onclick={() => (creating = true)}
        >
          <Icon name="plus" size={22} strokeWidth={2} />
        </button>
      {/snippet}
    </MobileTopBar>
  {:else}
    <header class="page-header">
      <div class="header-left">
        <Link href="/" class="logo">
          <img src={momentiumLogo} alt="Momentium" class="logo-icon" />
          <span class="logo-text">Momentium</span>
        </Link>
        <NavDropdown activePage="boards" />
      </div>
      <ProfileDropdown />
    </header>
  {/if}

  <main class="page-body">
    {#if !$viewport.isMobile}
      <div class="title-row">
        <div>
          <h1>Boards</h1>
          <p class="subtitle">Freeform canvases for arranging projects however you think.</p>
        </div>
        {#if !creating}
          <button class="new-btn" type="button" onclick={() => (creating = true)}>New board</button>
        {/if}
      </div>
    {/if}

    {#if creating}
      <form class="new-form" onsubmit={createBoard}>
        <!-- svelte-ignore a11y_autofocus -->
        <input
          autocomplete="off"
          bind:value={newName}
          class="new-input"
          type="text"
          placeholder="Board name"
          autofocus={!$coarsePointer}
          aria-label="Board name"
        />
        <button class="new-btn" type="submit" disabled={!newName.trim()}>Create</button>
        <button
          class="cancel-btn"
          type="button"
          onclick={() => {
            creating = false;
            newName = '';
          }}>Cancel</button
        >
      </form>
    {/if}

    {#if boards.length === 0}
      <div class="empty">
        <p>No boards yet.</p>
        <p class="empty-hint">
          Create one to place project cards on a canvas alongside labels and shapes.
        </p>
      </div>
    {:else}
      <ul class="board-grid">
        {#each boards as board (board.id)}
          <li class="board-tile">
            <Link href="/boards/{board.id}" class="board-link">
              <span class="board-name">{board.name}</span>
              <span class="board-meta">
                {board.itemCount}
                {board.itemCount === 1 ? 'item' : 'items'} · {formatUpdated(board.updatedAt)}
              </span>
            </Link>
            <button
              class="board-delete"
              type="button"
              onclick={() => deleteBoard(board)}
              aria-label="Delete {board.name}"
              title="Delete board">×</button
            >
          </li>
        {/each}
      </ul>
    {/if}
  </main>
</div>

<style>
  .page {
    min-height: 100%;
    background: #f9fafb;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.page-header .logo) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    text-decoration: none;
    color: #1f2937;
  }

  .logo-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .logo-text {
    font-size: 0.8125rem;
    font-weight: 700;
  }

  .page-body {
    max-width: 60rem;
    margin: 0 auto;
    padding: 1.5rem 1rem;
  }

  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .subtitle {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.125rem 0 0;
  }

  .new-form {
    display: flex;
    gap: 0.375rem;
    margin-bottom: 1rem;
  }

  .new-input {
    flex: 1;
    max-width: 20rem;
    padding: 0.3125rem 0.5rem;
    font-size: 0.8125rem;
    font-family: inherit;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
  }

  .new-input:focus {
    outline: none;
    border-color: #2563eb;
  }

  .new-btn {
    padding: 0.3125rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    font-family: inherit;
    border: none;
    border-radius: 0.25rem;
    background: #2563eb;
    color: white;
    cursor: pointer;
    white-space: nowrap;
  }

  .new-btn:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .new-btn:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }

  .cancel-btn {
    padding: 0.3125rem 0.75rem;
    font-size: 0.75rem;
    font-family: inherit;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    background: white;
    color: #6b7280;
    cursor: pointer;
  }

  .empty {
    padding: 2.5rem 1rem;
    text-align: center;
    background: white;
    border: 1px dashed #e5e7eb;
    border-radius: 0.5rem;
    color: #6b7280;
    font-size: 0.8125rem;
  }

  .empty p {
    margin: 0;
  }

  .empty-hint {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 0.25rem !important;
  }

  .board-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
    gap: 0.625rem;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .board-tile {
    position: relative;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
  }

  .board-tile:hover {
    border-color: #c7d2fe;
  }

  :global(.board-tile .board-link) {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    padding: 0.75rem;
    text-decoration: none;
  }

  .board-name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1f2937;
  }

  .board-meta {
    font-size: 0.6875rem;
    color: #9ca3af;
  }

  .board-delete {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    border: none;
    background: none;
    font-size: 1rem;
    line-height: 1;
    color: #d1d5db;
    cursor: pointer;
    padding: 0.125rem 0.25rem;
  }

  .board-delete:hover {
    color: #dc2626;
  }

  /* Phones (ma-inzp): 16px field (no iOS zoom-on-focus), 44px targets. The × keeps
     its look; its hit area grows with ::after. */
  @media (max-width: 767px) {
    .new-form {
      flex-wrap: wrap;
      align-items: center;
    }

    .new-input {
      flex: 1 1 100%;
      max-width: none;
      min-height: 44px;
      padding: 0 0.75rem;
      font-size: 16px;
      border-radius: 0.5rem;
    }

    .new-form .new-btn,
    .new-form .cancel-btn {
      flex: 1 1 0;
      min-height: 44px;
      font-size: 0.875rem;
    }

    :global(.board-tile .board-link) {
      min-height: 44px;
      padding-right: 3rem;
    }

    .board-delete {
      top: 0;
      right: 0;
      width: 44px;
      height: 44px;
      padding: 0;
      font-size: 1.25rem;
    }
  }
</style>
